import { useState, useEffect } from 'react';
import Reveal from '@/components/base/Reveal';
import Footer from '@/pages/home/components/Footer';
import Logo from '@/components/base/Logo';
import SmartLink from '@/components/base/SmartLink';
import CtaSection from '../home/components/CtaSection';

function ScrollNavbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Our Mission', href: '/mission' },
    { label: 'What We Do', href: '/projects' },
    { label: 'About Us', href: '/about' },
  ];

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-md border-b border-white/5' : ''
        }`}
    >
      <div className="px-6 md:px-12 lg:px-16 pt-6 pb-4">
        <nav
          className={`rounded-xl px-4 py-2 flex items-center justify-between transition-all duration-300 ${scrolled ? 'bg-transparent' : 'liquid-glass'
            }`}
        >
          <SmartLink href="/">
            <Logo className="h-5 sm:h-6 md:h-7 w-auto text-white shrink-0" />
          </SmartLink>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <SmartLink
                key={link.label}
                href={link.href}
                className="text-sm text-white/80 hover:text-gray-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </SmartLink>
            ))}
          </div>
          <a href="mailto:hello@sapphiremedia.group">
            <button className="bg-white text-black px-3 py-1.5 sm:px-5 sm:py-2 md:px-6 rounded-lg text-xs sm:text-sm font-medium hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-1 sm:gap-1.5">
              Reach out
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" className="size-3.5 sm:size-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </button>
          </a>
        </nav>
      </div>
    </div>
  );
}

const pillars = [
  { number: '01', title: 'On the Air',  body: 'Live and recorded shows hosted and produced by young people.' },
  { number: '02', title: 'In the Code', body: 'Open-source software shipped by student engineers and mentors.' },
  { number: '03', title: 'In the World', body: 'Stories, journalism, and projects that connect to real communities.' },
];

const principles = [
  { label: 'Youth at the mic', desc: 'Young people lead the work - adults coach, fund, and get out of the way.' },
  { label: 'Build in the open', desc: 'Source code, finances, and decisions live in public. Transparency is the default.' },
  { label: 'Craft over hype', desc: 'We ship things that last longer than a trend; software, stories, and shows worth keeping.' },
  { label: 'Access by default', desc: 'No paywalls, no pedigrees, no gatekeeping. If you want in, you’re in.' },
];

export default function VisionPage() {
  return (
    <div className="bg-black min-h-screen">
      <ScrollNavbar />

      {/* 1 — Hero */}
      <section className="relative pt-44 pb-24 px-6 md:px-12 lg:px-16">
        <div className="max-w-7xl mx-auto lg:grid lg:grid-cols-2 lg:gap-20 lg:items-end">
          <div>
            <Reveal variant="fadeUp" duration={800}>
              <p className="text-xs tracking-[0.3em] text-white/60 uppercase mb-6">Our Mission</p>
            </Reveal>
            <Reveal variant="fadeUp" delay={100} duration={900}>
              <h1
                className="text-5xl md:text-6xl lg:text-7xl font-normal text-white font-['Instrument_Serif']"
                style={{ letterSpacing: '-0.02em' }}
              >
                What we are<br />committed to.
              </h1>
            </Reveal>
          </div>
          <Reveal variant="fadeUp" delay={250} duration={800}>
            <p className="text-base md:text-lg text-white/55 leading-relaxed mt-10 lg:mt-0 max-w-md">
              We exist to raise awareness and motivate youth to get out there. We give youth a voice and a place to be themselves, grow, and do something.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 2 — Image strip */}
      <section className="px-6 md:px-12 lg:px-16 pb-24">
        <div className="max-w-7xl mx-auto">
          <Reveal variant="scaleUp" duration={1000}>
            <div className="relative w-full h-[400px] md:h-[540px] rounded-2xl overflow-hidden">
              <img
                src="/view-diverse-adolescents-practicing-health-wellness-activities-themselves-their-community.jpg"
                alt="Happy people"
                className="w-full h-full object-cover object-center opacity-75"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/20" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3 — Pillars */}
      <section className="py-24 px-6 md:px-12 lg:px-16 bg-[#080808]">
        <div className="max-w-7xl mx-auto">
          <Reveal variant="fadeUp" duration={700}>
            <p className="text-xs tracking-[0.3em] text-white/60 uppercase mb-14">Core Principles</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
            {pillars.map((pillar, i) => (
              <Reveal key={pillar.number} variant="fadeUp" delay={i * 120} duration={800}>
                <div className="bg-[#080808] p-10 md:p-12 group hover:bg-white/[0.02] transition-colors duration-300 h-full">
                  <span className="text-xs text-white/45 font-mono block mb-8">{pillar.number}</span>
                  <h3 className="text-2xl font-medium text-white font-['Instrument_Serif'] mb-4" style={{ letterSpacing: '-0.02em' }}>{pillar.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{pillar.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — How We Think */}
      <section className="py-28 px-6 md:px-12 lg:px-16">
        <div className="max-w-7xl mx-auto lg:grid lg:grid-cols-2 lg:gap-24 lg:items-start">
          {/* Left */}
          <div className="mb-16 lg:mb-0 lg:sticky lg:top-32">
            <Reveal variant="fadeRight" duration={800}>
              <p className="text-xs tracking-[0.3em] text-white/60 uppercase mb-6">How We Think</p>
            </Reveal>
            <Reveal variant="fadeRight" delay={100} duration={900}>
              <h2
                className="text-5xl md:text-6xl font-normal text-white font-['Instrument_Serif']"
                style={{ letterSpacing: '-0.02em' }}
              >
                Principles that<br />guide every move.
              </h2>
            </Reveal>
            <Reveal variant="fadeRight" delay={220} duration={800}>
              <p className="mt-6 text-sm text-white/55 leading-relaxed max-w-sm">
                These aren’t slogans on a wall, they’re the rules we hold each other to. Four beliefs that shape every show we record, every line of code we ship, and every kid we hand a microphone to.
              </p>
            </Reveal>
          </div>

          {/* Right — principles list */}
          <div className="divide-y divide-white/5">
            {principles.map((item, i) => (
              <Reveal key={item.label} variant="fadeLeft" delay={i * 130} duration={800} threshold={0.1}>
                <div className="py-8 flex items-start gap-6 group cursor-default">
                  <span className="text-xs text-white/30 font-mono mt-1 shrink-0 w-5">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h4 className=" text-xl font-medium text-white mb-1 group-hover:text-white/80 transition-colors duration-200 font-['Instrument_Serif']">
                      {item.label}
                    </h4>
                    <p className="text-sm text-white/55">{item.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — Closing statement */}
      {/* <section className="py-28 px-6 md:px-12 lg:px-16 bg-[#080808] border-t border-white/5">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal variant="fadeUp" duration={900}>
            <blockquote
              className="text-3xl md:text-4xl lg:text-5xl font-light text-white leading-tight"
              style={{ letterSpacing: '-0.03em' }}
            >
              We believe capital should serve people,<br className="hidden md:block" /> not the other way around.
            </blockquote>
          </Reveal>
          <Reveal variant="fadeUp" delay={150} duration={800}>
            <div className="mt-10 flex items-center justify-center gap-4">
              <div className="w-px h-6 bg-white/35" />
              <span className="text-sm text-white/55">FinVibe Founding Principle</span>
              <div className="w-px h-6 bg-white/35" />
            </div>
          </Reveal>
          <Reveal variant="fadeUp" delay={280} duration={800}>
            <div className="mt-12">
              <a
                href="/"
                className="inline-block bg-white text-black px-10 py-4 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Start a Conversation
              </a>
            </div>
          </Reveal>
        </div>
      </section> */}

      <CtaSection />

      <Footer />
    </div>
  );
}
